import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { map } from 'rxjs/operators';
import { ProductOverviewResponse } from './product-overview-response.model';
import { PONodeComponent } from 'src/app/components/po-treeview/abstract-classes/po-node/ponode.component';
import { environment } from '../../../environments/environment';
import { ArrangementLevelComponent } from '../../components/po-treeview/abstract-classes/arrangement-level/arrangement-level.component';
import { ProductOverviewRequest } from './product-overview-request.model';

@Injectable({
  providedIn: 'root',
})
export class ProductOverviewService {

  civProductOverviewURL: string;
  lazyDetailsURL: string;
  lazyArrangementURL: string;
  arrangementURL: string;
  sumOfAmountsURL: string;

  constructor(private http: HttpClient) {
    const baseUrl = environment.civServicesBaseURL;
    this.civProductOverviewURL = baseUrl + 'productoverview';
    this.lazyDetailsURL = baseUrl + 'productoverview/details';
    this.lazyArrangementURL = baseUrl + 'productoverview/node';
    this.arrangementURL = baseUrl + 'getarrangementurl';
    this.sumOfAmountsURL = baseUrl + 'sumofamounts';
  }

  // Helper to set Content-Type (Required because your body is a JSON string)
  // We DO NOT set Authorization here. The MSAL Interceptor does that automatically.
  private getJsonHeaders(): HttpHeaders {
    return new HttpHeaders({
      'Content-type': 'application/json'
    });
  }

  getHierarchyNodes() {
    const requestBody = this.createRequestBody();
    // Pass getJsonHeaders() to ensure server knows it's JSON
    return this.http.post<any>(this.civProductOverviewURL, requestBody, { headers: this.getJsonHeaders() });
  }

  getSumOfAmounts(keys: any[], productNodeNameRestrictedDataViewExceptionMap: any) {
    const requestBody = this.createSumOfAmountsRequestBody(keys, productNodeNameRestrictedDataViewExceptionMap);
    return this.http.post<any>(this.sumOfAmountsURL, requestBody, { headers: this.getJsonHeaders() });
  }

  getDetails(listOfArrangement: any, parentNode: any) {
    const requestBody = this.createDetailRequestBody(parentNode, listOfArrangement);

    return this.http.post<any>(this.lazyDetailsURL, requestBody, { headers: this.getJsonHeaders() })
      .pipe(
        map((res: any) => {
          const listOfDetails: any[] = [];
          if (res['data']) {
            res['data'].forEach((details: any) => {
              listOfDetails.push(details);
            });
          }
          return listOfDetails;
        })
      );
  }

  getArrangements(arno: string, admincd: string, parentNode: any) {
    const requestBody = this.createArrangementRequestBody(parentNode, arno, admincd);

    return this.http.post(this.lazyArrangementURL, requestBody, { headers: this.getJsonHeaders() })
      .pipe(
        map((res: any) => {
          const nodeChildren: PONodeComponent[] = [];
          if (res['children'] && res['children'].length) {
            (res['children'] as any[]).forEach((child: PONodeComponent) => {
              if (child.data) {
                child.data.number = '';
              }
              nodeChildren.push(child);
            });
          }
          return nodeChildren;
        })
      );
  }

  getArrangementUrl(arno: string, admincd: string, bankCode: string) {
    const requestBody = this.createArrangementUrlRequestBody(arno, admincd, bankCode);
    return this.http.post(this.arrangementURL, requestBody, { headers: this.getJsonHeaders() });
  }

  // --- Helper Methods (Logic Unchanged) ---

  createArrangementUrlRequestBody(arno: string, admincd: string, bankCode: string): string | undefined {
    // Note: If ProductOverviewRequest is undefined, this returns undefined.
    // Ensure your component initializes this data before calling the service.
    if (!ProductOverviewRequest) {
      return undefined;
    }
    const perspective = this.determinePerspective();
    const requestBody = {
      requestingSystem: 'Siebel',
      perspective,
      arrangementNumber: arno,
      administrationCode: admincd,
      bankCode,
    };
    return JSON.stringify(requestBody);
  }

  createRequestBody(): string | undefined {
    if (!ProductOverviewRequest) {
      return undefined;
    }
    const perspective = this.determinePerspective();
    const requestBodyContent = {
      requestingSystem: 'Siebel',
      perspective,
      parametersToken: ProductOverviewRequest.parametersToken,
    };
    return JSON.stringify(requestBodyContent);
  }

  private createSumOfAmountsRequestBody(keys: any[], productNodeNameRestrictedDataViewExceptionMap: any): string | undefined {
    if (keys.length > 0) {
      const mapAsObject = this.mapToObject(productNodeNameRestrictedDataViewExceptionMap);
      const requestBody = {
        keys,
        relationRestrictedDataView: ProductOverviewResponse.relationRestrictedDataView,
        relationMarketSegmentCode: ProductOverviewResponse.relationMarketSegmentCode || '',
        restrictedDataViewExceptionMap: mapAsObject,
      };
      return JSON.stringify(requestBody);
    } else {
      return undefined;
    }
  }

  private mapToObject(productNodeNameRestrictedDataViewExceptionMap: any) {
    const obj = Object.create(null);
    if (productNodeNameRestrictedDataViewExceptionMap && typeof productNodeNameRestrictedDataViewExceptionMap.forEach === 'function') {
      productNodeNameRestrictedDataViewExceptionMap.forEach((value: boolean, key: string) => {
        obj[key] = value;
      });
    }
    return obj;
  }

  createArrangementRequestBody(parentNode: ArrangementLevelComponent, arno: string, admincd: string): string | undefined {
    if (ProductOverviewRequest && arno && admincd) {
      const requestBody = {
        requestingSystem: 'Siebel',
        perspective: this.determinePerspective(),
        arrangementNumber: arno,
        administrationCode: admincd,
        relationRestrictedDataView: ProductOverviewResponse.relationRestrictedDataView,
        restrictedDataViewException: parentNode.data.restrictedDataViewException,
        relationMarketSegmentCode: ProductOverviewResponse.relationMarketSegmentCode || '',
        parametersToken: ProductOverviewRequest.parametersToken,
      };
      return JSON.stringify(requestBody);
    } else {
      return undefined;
    }
  }

  createDetailRequestBody(parentNode: any, listOfArrangement: any[]): string | undefined {
    if (parentNode && listOfArrangement.length > 0) {
      const requestBodyContent = {
        requestingSystem: 'Siebel',
        perspective: this.determinePerspective(),
        listOfArrangement,
        relationRestrictedDataView: ProductOverviewResponse.relationRestrictedDataView,
        restrictedDataViewException: parentNode.data.restrictedDataViewException,
        relationMarketSegmentCode: ProductOverviewResponse.relationMarketSegmentCode || '',
        parametersToken: ProductOverviewRequest.parametersToken,
      };
      return JSON.stringify(requestBodyContent);
    } else {
      return undefined;
    }
  }

  determinePerspective() {
    let perspective;
    if (ProductOverviewRequest.orgYN === 'Y') {
      perspective = 'ORG';
    } else {
      perspective = 'NP';
    }
    return perspective;
  }
}
